/**
 * AB#2052 — Appointment Time Integrity (Batch 1 of Epic AB#2055).
 *
 * Architectural decision (Faris): for the UAE-only controlled pilot an
 * appointment's datetime is STORED and RETURNED stamped `…Z`, but it
 * represents the salon's WALL-CLOCK time — not an instant in true UTC.
 * A `10:00Z` booking is a 10:00 booking at the salon. The portal + admin must
 * therefore render appointment datetimes AS wall-clock: extract/format them
 * in UTC and NEVER convert to the viewer's local timezone. Rendering
 * viewer-local turned `10:00Z` into "2:00 PM" for a UAE (+4) viewer.
 *
 * Every formatter here pins `timeZone: 'UTC'`, so the wall-clock value is
 * preserved no matter where the viewer's browser sits. This is display-only:
 * it does NOT touch booking creation, availability, or the backend contract.
 *
 * Locale is still honoured (AR/EN + RTL) — only the timezone is fixed.
 */

/** Loose locale hint from `LanguageContext` ('ar' | 'en' | anything). */
export type AppointmentLocale = string | null | undefined;

/** Fixed timezone for every appointment-facing formatter. */
const WALL_CLOCK_TZ = 'UTC';

/** BCP-47 tag for date/date-time formatting (Arabic vs. day-month-year English). */
function dateTag(locale: AppointmentLocale): string {
    return locale === 'ar' ? 'ar' : 'en-GB';
}

/** True when `iso` parses to a real instant. */
export function isValidAppointmentInstant(iso: string): boolean {
    return !Number.isNaN(new Date(iso).getTime());
}

export interface FormatAppointmentTimeOptions {
    /** Locale for the time string; 'ar' → Arabic locale, else en-US. */
    locale?: AppointmentLocale;
    /** 12-hour clock with AM/PM (default true — matches the legacy list views). */
    hour12?: boolean;
    /**
     * Snap to the nearest 15 minutes — preserves the legacy schedule-grid
     * display behaviour of the two `/appointments` schedule pages. The snap is
     * computed on the WALL-CLOCK (UTC) minutes so it matches the shown value.
     */
    round15?: boolean;
}

/**
 * Wall-clock time of an appointment, e.g. `10:00Z` → "10:00 AM" (en) — NEVER
 * shifted to the viewer's timezone.
 */
export function formatAppointmentTime(
    iso: string,
    options: FormatAppointmentTimeOptions = {},
): string {
    const { locale, hour12 = true, round15 = false } = options;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return 'Invalid Time';

    if (round15) {
        const rounded = Math.round(date.getUTCMinutes() / 15) * 15;
        date.setUTCMinutes(rounded === 60 ? 0 : rounded);
        if (rounded === 60) date.setUTCHours(date.getUTCHours() + 1);
    }

    return date.toLocaleTimeString(locale === 'ar' ? 'ar' : 'en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12,
        timeZone: WALL_CLOCK_TZ,
    });
}

/**
 * Wall-clock calendar date of an appointment, `DD/MM/YYYY` (en-GB) — extracted
 * in UTC so a late-evening `…T22:00:00Z` booking never rolls onto the wrong day.
 */
export function formatAppointmentDate(iso: string, locale?: AppointmentLocale): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString(dateTag(locale), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: WALL_CLOCK_TZ,
    });
}

/**
 * Wall-clock date + time of an appointment (default: medium date + short time),
 * extracted in UTC. Pass `overrides` to shape the parts; `timeZone` is always
 * forced to UTC and cannot be overridden.
 */
export function formatAppointmentDateTime(
    iso: string,
    locale?: AppointmentLocale,
    overrides?: Intl.DateTimeFormatOptions,
): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    const opts: Intl.DateTimeFormatOptions = overrides
        ? { ...overrides, timeZone: WALL_CLOCK_TZ }
        : { dateStyle: 'medium', timeStyle: 'short', timeZone: WALL_CLOCK_TZ };
    return new Intl.DateTimeFormat(dateTag(locale), opts).format(date);
}
